/**
 * Record and Type over the town: home's two ways in open this panel, which slides up over home's live town (the town
 * stays drawn behind it, dimmed) with the same capture form as the record screen (`<capture-form>`, capture-form.ts):
 * drafts, retries, 402 and 429, the allowance, the review gate and the wait all behave as there. While a save is in
 * flight the wait shows in the panel over the town, and a saved moment goes to its landing (`/land/<id>`) from here.
 *
 * The panel is a history entry on home (`/#record`, `/#type`, and the lantern's `/#listen`, ticket 44), so the phone's Back closes it, as do its close button
 * and Escape; it works on the empty land too. The record screen (`/record`, `/record#wanted`) stays for bookmarks and
 * the onboarding. Under reduced motion it fades in instead of sliding. Closing it (either way, or leaving home) while
 * the form holds a recording that is not kept anywhere yet asks first; it fires `capture-close` once closed. Focus
 * moves into the panel as it opens and back to what opened it as it closes.
 *
 * Answer them (v1 ticket 42): `/#type?answer=<id>` opens Type answering that newcomer moment's question, so a reload
 * keeps it; closing the panel drops it with the entry. `openAnswer(id)` opens it from anywhere (the landing's sign,
 * the moment screen).
 */
import { LitElement, html, nothing } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { customElement, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { dropShared } from '../share-inbox';
import { flagRead, loadOneButton, oneButtonOn } from '../one-button';
import { setLeaveGuard } from '../router';
import { cancelWrittenAttempt } from '../research-client';
import { type CaptureTabName } from '../newcomers';
import { openCapture, routeHere, takeBloom } from './capture-open';
import { focusBack } from './home-hash';
import './capture-form';
import type { CaptureForm } from './capture-form';
import type { ListenPanel } from './listen/listen-panel';

export { openAnswer, openCapture } from './capture-open';

@customElement('capture-sheet')
export class CaptureSheet extends LitElement {
  @state() private tab: CaptureTabName | null = routeHere()?.tab ?? null;
  /** The newcomer moment the Type tab answers (v1 ticket 42), from the URL. */
  @state() private answer = routeHere()?.answer ?? '';
  @state() private draft = routeHere()?.draft;
  /** The tab the form shows (it can switch inside): the header reads Answer them only on Type. */
  @state() private formTab = '';
  /** What had focus when the panel opened, to go back to as it closes. */
  private returnTo: HTMLElement | null = null;
  private wasOpen = false;
  /** The tab the panel last showed: closing it from `shared` drops the stashed voice message. */
  private lastTab: CaptureTabName | null = null;
  private flag: 'idle' | 'loading' | 'done' = 'idle';

  protected createRenderRoot() { return this; }

  /** The question to ask before the panel (or home) goes, when its form holds an unkept recording. */
  private guard = () => {
    if (!this.tab) return null;
    const at = this.querySelector<CaptureForm>('capture-form')?.atStake() || this.querySelector<ListenPanel>('listen-panel')?.atStake();
    return at ? t('record.leaveUnsaved') : null;
  };

  private mayClose(): boolean {
    const q = this.guard();
    return !q || confirm(q);
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.onPop);
    window.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.onPop);
    window.removeEventListener('keydown', this.onKey);
    document.documentElement.classList.remove('capture-open');
    if (this.wasOpen) setLeaveGuard(null);
    this.wasOpen = false;
  }

  private onPop = () => {
    // The app's router checks the leave guard first, including hash changes; read its accepted or restored URL.
    const next = routeHere();
    const cancelledWritten = this.tab === 'wanted' && !next;
    this.tab = next?.tab ?? null;
    this.answer = next?.answer ?? '';
    this.draft = next?.draft;
    if (cancelledWritten) cancelWrittenAttempt();
  };

  private onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.tab) this.close();
  };

  /** Back to home: the entry the panel pushed goes, or, opened straight from its URL, the URL loses its hash. */
  private close = () => {
    if (!this.mayClose()) return;
    const cancelledWritten = this.tab === 'wanted' || this.formTab === 'wanted';
    // Asked already: the Back below passes without asking again.
    this.tab = null;
    this.answer = '';
    this.draft = undefined;
    if (cancelledWritten) cancelWrittenAttempt();
    if ((history.state as { capture?: unknown } | null)?.capture) history.back();
    else history.replaceState(null, '', '/');
  };

  protected updated() {
    if (this.tab) this.lastTab = this.tab;
    const open = !!this.tab;
    document.documentElement.classList.toggle('capture-open', open);
    if (open === this.wasOpen) return;
    this.wasOpen = open;
    if (open) {
      // Leaving home while it is up (a link, a reload) asks too.
      setLeaveGuard(this.guard);
      this.bloomOut();
      const at = document.activeElement;
      this.returnTo = at instanceof HTMLElement && at !== document.body ? at : null;
      this.querySelector<HTMLElement>('.capture-close')?.focus();
    } else {
      if (this.lastTab === 'shared') void dropShared();
      setLeaveGuard(null);
      focusBack(this.returnTo);
      this.returnTo = null;
      this.dispatchEvent(new CustomEvent('capture-close', { bubbles: true, composed: true }));
    }
  }

  /**
   * Opened by a tap on home's lantern: the panel blooms out of it, a circle growing from the lantern's centre, in place
   * of sliding up (reduced motion keeps the fade).
   */
  private bloomOut() {
    const from = takeBloom(), panel = this.querySelector<HTMLElement>('.capture-panel');
    if (!from || !panel || this.tab !== 'listen') return;
    // The bloom class first: the slide-up's start (translateY(100%)) would put the measured rect a panel too low.
    panel.classList.add('bloom');
    const r = panel.getBoundingClientRect();
    // From the panel's foot, which stays put while the lantern's panel grows into its content.
    panel.style.setProperty('--bloom-at', `${Math.round(from.x - r.left)}px calc(100% - ${Math.round(r.bottom - from.y)}px)`);
  }

  render() {
    if (!this.tab) return nothing;
    // The lantern is one flag's: read it before anything shows, and with it off `#listen` is the record tab as before.
    // Once: a health read that fails must not send this render round again; the classic panel shows instead.
    if (this.tab === 'listen' && !flagRead() && this.flag !== 'done') {
      if (this.flag === 'idle') {
        this.flag = 'loading';
        void loadOneButton().then(() => { this.flag = 'done'; this.requestUpdate(); });
      }
      return nothing;
    }
    const unavailable = this.tab === 'listen' && this.draft !== undefined && !oneButtonOn();
    const tab: CaptureTabName = this.tab === 'listen' && !oneButtonOn() && !unavailable ? 'record' : this.tab;
    // The lantern (ticket 44): its panel is its own chunk, loaded on the first tap.
    if (tab === 'listen' && !unavailable) void import('./listen/listen-panel');
    return html`
      <div class="capture-dim" aria-hidden="true"></div>
      <section class="capture-panel ${tab === 'listen' ? 'cs-lantern' : ''}" role="dialog" aria-modal="true" aria-labelledby="capture-title">
        <div class="capture-head">
          <h2 id="capture-title">${t(tab === 'listen' ? 'listen.title' : this.answer && this.formTab !== 'record' ? 'record.answerTitle' : 'record.title')}</h2>
          <button class="btn ghost icon capture-close" @click=${this.close} aria-label=${t('tend.close')}>✕</button>
        </div>
        ${tab === 'listen'
          ? unavailable ? html`<div class="ls-draft-unavailable" role="alert">${t('listen.unheard.listenUnavailable')}
              <button class="btn ls-drafts" @click=${() => openCapture('wanted')}>${t('drafts.manage')}</button></div>`
            : keyed(this.draft, html`<listen-panel .draftId=${this.draft} @capture-dismiss=${this.close} @listen-quill=${() => openCapture('wanted')}></listen-panel>`)
          : html`<capture-form .opensOn=${tab} .answer=${this.answer} @capture-dismiss=${this.close} @capture-tab=${(e: CustomEvent<string>) => (this.formTab = e.detail)} @capture-alternative=${(e: CustomEvent<'record' | 'wanted'>) => openCapture(e.detail)}></capture-form>`}
      </section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'capture-sheet': CaptureSheet }
}
